import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import PageContainer from "../../components/page-container";
import s from "./styles.module.scss";
import PageHeader from "../../components/page-header";
import { ReactComponent as InfoIcon } from "../../assets/info.svg";
import { useJobPolling } from "../../hooks/use-job-polling";


export const Loading = ({ text, description }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { getToken } = useAuth();
  const hasNavigatedRef = useRef(false);

  const { jobId, calendar, totalEvents: initialTotalEvents } = state || {};

  // Only poll if we have a jobId (new flow)
  // Old flow might navigate here directly without jobId
  const isJobPolling = !!jobId;
  const [activeJobId, setActiveJobId] = useState(isJobPolling ? jobId : null);

  // Always call the hook (React rules), but pass null jobId if not polling
  const pollingResult = useJobPolling(
    activeJobId,
    getToken,

    // On completion - navigate to events-added
    (batch_id, total_events) => {
      if (!hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        console.log('[LOADING] Job completed, navigating to events-added');
        setActiveJobId(null); // Stop polling IMMEDIATELY

        localStorage.removeItem("processingJobId");
        localStorage.removeItem("processingCalendar");
        localStorage.removeItem("selectedSections");
        localStorage.removeItem("allSectionsData");
        localStorage.removeItem("userCourseData");
        sessionStorage.removeItem("selectedSections");
        sessionStorage.removeItem("allSectionsData");
        sessionStorage.removeItem("userCourseData");

        // Clear calendar caches to ensure fresh event data appears
        localStorage.removeItem('ocean_calendar_events');
        localStorage.removeItem('ocean_calendar_ranges');
        localStorage.removeItem('ocean_calendar_timestamp');

        // Signal to calendar that suggestions may be generating (worker queues them after insert)
        sessionStorage.setItem('just_added_course', 'true');

        navigate('/events-added', {
          replace: true,
          state: {
            calendar: calendar,
            webcalUrl: null,
            totalEvents: total_events,
            message: "Events added successfully!",
          },
        });
      }
    },

    // On error
    (error) => {
      if (!hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        console.error('[LOADING] Job failed:', error);
        setActiveJobId(null); // Stop polling

        localStorage.removeItem("processingJobId");
        localStorage.removeItem("processingCalendar");
        navigate('/no-events', {
          replace: true,
          state: {
            courseName: "Calendar Insertion",
            errorMessage: error || "Failed to add events to calendar",
          },
        });
      }
    }
  );

  const { message, totalEvents: polledTotalEvents } = pollingResult;

  // Use polled value if available, otherwise fall back to initial value from navigation state
  const totalEvents = polledTotalEvents || initialTotalEvents || 0;

  // Format calendar name for display
  const calendarDisplayName = calendar
    ? calendar.charAt(0).toUpperCase() + calendar.slice(1)
    : '';

  // Build display message - use polling message, or build from totalEvents
  const displayMessage = message ||
    (totalEvents > 0 ? `Adding ${totalEvents} event${totalEvents !== 1 ? 's' : ''} to your ${calendarDisplayName} Calendar...` : null) ||
    state?.message ||
    text ||
    "Adding events to your calendar...";

  // Legacy behavior: navigate after timeout if successRoute is provided
  useEffect(() => {
    if (!isJobPolling && state?.successRoute) {
      setTimeout(() => {
        navigate(state?.successRoute, { state: state?.successState });
      }, 2000);
    }
  }, [state?.successRoute, state?.successState, navigate, isJobPolling]);

  return (
    <PageContainer>
      <PageHeader
        title={displayMessage}
        description={state?.description || description}
      />

      <div className={s.infoMessage}>
        <InfoIcon className={s.infoIcon} />
        <span>Please do not refresh this page</span>
      </div>

      <div className={s.dotsLoader}>
        <div className={`${s.dot} ${s.dot1}`}></div>
        <div className={`${s.dot} ${s.dot2}`}></div>
        <div className={`${s.dot} ${s.dot3}`}></div>
        <div className={`${s.dot} ${s.dot4}`}></div>
      </div>
    </PageContainer>
  );
};
